// Boot-time wiring for the commerce billing UI ports. These
// members are reached by non-settings callers — global modals, upgrade flows —
// so they must be live before any lazy settings screen renders. Imported at
// module scope in `_app.tsx`, right after `commerce-data-wiring`.
import walkthroughPoster from '@/assets/videos/evidence/walkthrough-poster.webp';
import {
  evidencePostUpgradeOnboardingModalOpenAtom,
  evidencePostUpgradeWelcomeDeferQueueUntilAfterOnboardingAtom,
} from '@/atoms/evidencePostUpgradeOnboardingAtoms';
import { trialExtensionModalOpenAtom } from '@/atoms/freeTrialModalAtoms';
import {
  dequeueModalAtom,
  enqueueModalAtom,
  seenModalIdsAtom,
} from '@/atoms/modalQueueAtoms';
import {
  clinicianMaxUpgradeModalEntryPointAtom,
  clinicianUpgradeModalOpenAtom,
  practiceUpgradeModalOpenAtom,
  trialModalPreviewDaysLeftAtom,
  upgradeModalFromTrialRecapAtom,
  usePaywallStates,
} from '@/atoms/paywallAtoms';
import { ExternalLink } from '@/components/common/external-link';
import {
  BILLING_PLANS_PRICING_URL,
  FREE_VS_PRO_PLANS_ACCOUNT_ANCHOR_HELP_ARTICLE_PATH,
  GET_IN_TOUCH_URL,
  getBillingHelpCentreUrl,
  getBillingPlansPricingUrl,
  getComparePlansUrl,
  getGetInTouchUrl,
  getUsageLimitsHelpUrl,
} from '@/components/constants/billing-urls';
import { useIsGamifyTrialExtensionEntryEnabled } from '@/components/layout/Navigation/use-is-gamify-trial-extension-entry-enabled';
import { HeidiUpgradeBaseModal } from '@/components/modal/heidi-upgrade-base-modal/heidi-upgrade-base-modal';
import { PriceDisplay } from '@/components/modal/heidi-upgrade-base-modal/price-display';
import { TrialStartModalBase } from '@/components/modal/trial-start-modal-base';
import { StackedSourceAvatars } from '@/components/ui/floating-ask-heidi/evidence-prompts-bar/stacked-source-avatars';
import { PhoneInput } from '@/components/ui/phone-input';
import { USAGE_POLICY } from '@/components/utils/legalDocuments';
import { useTrialRecapUsageStats } from '@/composition/hosts/commerce/trials-and-upgrades/useTrialRecapUsageStats';
import { useIsGamifyTrialArmAllowed } from '@/composition/hosts/growth/post-note-quality/hooks/use-is-gamify-trial-arm-allowed';
import { usePracticeInvite } from '@/composition/hosts/growth/practice-invite-team-members/usePracticeInvite';
import {
  inviteTeamStepCompletedAtom,
  practiceTrialModalPendingAtom,
} from '@/composition/hosts/growth/practice-invite/atoms';
import { useShowSessionsListAssistantView } from '@/composition/hosts/session/session-list/useShowSessionsListAssistantView';
import {
  IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED,
  IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED,
  IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_PRACTICE_INVITE_FLOW_ENABLED,
  useIsEvidenceQueryLimitsForLegacyTiersEnabled,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsHeidi2MemberUpgradeRequestEnabled,
  useIsHeidi2OptInEnabled,
  useIsHeidi2PracticeMaxUpgradeEnabled,
  useIsHeidi2PricingPlansModalEnabled,
  useIsHeidi2TeamUpgradesEnabled,
  useIsHeidi2TrialEndEnabled,
  useIsWorkspaceSettingsIaEnabled,
} from '@/features/flags';
import { getPrivacyPolicyLink } from '@/helpers/getPrivacyPolicyLink';
import { useRegion } from '@/helpers/getRegion';
import { getTermsOfUseLink } from '@/helpers/localiseHelper';
import { useManualExposure } from '@/hooks/amplitude';
import { useMutateApplySubscriptionDiscount } from '@/hooks/mutation/useMutateApplySubscriptionDiscount';
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useMutateSubmitSalesBookingForm } from '@/hooks/mutation/useMutateSubmitSalesBookingForm';
import { useQueryCreditAllowance } from '@/hooks/query/useQueryCreditAllowance';
import { useQueryGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import {
  formatPriceWithCurrency,
  getCurrencySymbol,
  getDisplayedPriceFromPlan,
  getNewProductTierProduct,
  getPlanFromPeriod,
  getProductByName,
  getProductUpgradeDisplayPrices,
  getTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQuerySourceRecommendations } from '@/hooks/query/useQuerySourceRecommendations';
import { useQueryStripeLink } from '@/hooks/query/useQueryStripeLink';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useFeatureLists } from '@/hooks/useFeatureLists';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { useShouldHideMarketRestrictedFeatures } from '@/hooks/useShouldHideMarketRestrictedFeatures';
import {
  getCurrentPlanNameForTrial,
  useUpgradeModalTrialText,
} from '@/hooks/useUpgradeModalTrialText';
import { useUserQuota } from '@/hooks/useUserQuota';
import { track, type TrackEvent } from '@/lib/analytics/track';
import { sleep } from '@/lib/utils';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { PaywallSource } from '@/models/analytics/features/payments';
import {
  trackClinicianTrialEndedClick,
  trackClinicianTrialEndedViewed,
} from '@/models/analytics/features/trackClinicianTrialEndedAnalytics';
import {
  trackEvidencePostUpgradeOnboardingClick,
  trackEvidencePostUpgradeWelcomeClick,
} from '@/models/analytics/features/trackEvidencePostUpgradeAnalytics';
import {
  trackNinetyDayTrialEndDiscreetClick,
  trackNinetyDayTrialEndDiscreetViewed,
  trackNinetyDayTrialEndingReminderClick,
  trackNinetyDayTrialEndingReminderDismissed,
  trackNinetyDayTrialEndingReminderViewed,
  trackNinetyDayTrialRecapClick,
  trackNinetyDayTrialRecapViewed,
  trackPracticeUpgradeModalContactSalesClick,
} from '@/models/analytics/features/trackNinetyDayTrialAnalytics';
import {
  trackPracticeInviteClick,
  trackPracticeInviteViewed,
  trackPracticeTrialClick,
  trackPracticeTrialViewed,
} from '@/models/analytics/features/trackPracticeTrialInviteAnalytics';
import {
  trackClinicianTrialEndedTeamUpgradeRequestSubmit,
  trackNinetyDayTrialRecapTeamUpgradeRequestSubmit,
  trackPracticeTrialEndedTeamUpgradeRequestSubmit,
  trackPricingPlansModalTeamUpgradeRequestSubmit,
  trackSettingsBillingTeamUpgradeRequestSubmit,
  trackTeamTrialUpgradeRequestViewed,
  trackUpgradeToPracticeModalTeamUpgradeRequestSubmit,
} from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import {
  trackTrialUpgradeRequestCallbackClick,
  trackTrialUpgradeRequestCallbackViewed,
} from '@/models/analytics/features/trackTrialUpgradeRequestCallbackAnalytics';
import { isTauri, openUrlInTauri } from '@/tauri/utils';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';
import { getHubSpotTrackingCookie } from '@/utils/hubspot/getHubSpotTrackingCookie';
import { mapOrganisationSizeToHubSpot } from '@/utils/mapOrganisationSizeToHubSpot';
import { normalizeCountryName } from '@/utils/normalizeCountryName';
import { getStripeCallbackRedirect, getStripeReturnRedirect } from '@/utils/scribeHTTP';
import {
  AppleIapInfoText,
  configureCommerceBillingUi,
  configureCommerceTrialsUi,
  type CommerceBillingUiPorts,
  type CommerceTrialsUiPorts,
} from '@heidi/commerce';
import { usePracticeInviteEmailForm } from '@heidi/growth';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { createElement, lazy, type ComponentType } from 'react';

// The thunk is not called here, so the imported binding stays unread until use.
function callThrough<T extends (...args: never[]) => unknown>(read: () => T): T {
  return ((...args: Parameters<T>) => read()(...args)) as T;
}

function componentThrough<P extends object>(
  read: () => ComponentType<P>
): ComponentType<P> {
  return function TrialsPortView(props) {
    return createElement(read(), props);
  };
}

configureCommerceBillingUi({
  AppleIapInfoText:
    AppleIapInfoText as unknown as CommerceBillingUiPorts['AppleIapInfoText'],
  ExternalLink,
  HeidiUpgradeBaseModal:
    HeidiUpgradeBaseModal as unknown as CommerceBillingUiPorts['HeidiUpgradeBaseModal'],
  PriceDisplay: PriceDisplay as unknown as CommerceBillingUiPorts['PriceDisplay'],
  useTrialExtensionModalOpenValue: () => useAtomValue(trialExtensionModalOpenAtom),
  useSetTrialExtensionModalOpen: () => useSetAtom(trialExtensionModalOpenAtom),
  usePracticeUpgradeModalState: () => useAtom(practiceUpgradeModalOpenAtom),
  useSetPracticeUpgradeModalOpen: () => useSetAtom(practiceUpgradeModalOpenAtom),
  useUpgradeModalFromTrialRecapState: () => useAtom(upgradeModalFromTrialRecapAtom),
  useTrialModalPreviewDaysLeftValue: () => useAtomValue(trialModalPreviewDaysLeftAtom),
  useSetTrialModalPreviewDaysLeft: () => useSetAtom(trialModalPreviewDaysLeftAtom),
  useSetClinicianMaxUpgradeModalEntryPoint: () =>
    useSetAtom(clinicianMaxUpgradeModalEntryPointAtom) as unknown as (
      entryPoint: string | null
    ) => void,
  useSetClinicianUpgradeModalOpen: () => useSetAtom(clinicianUpgradeModalOpenAtom),
  usePaywallStates,
  isBillingCustomDowngradeFlowsEnabled: () =>
    IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED() === true,
  isEndOf90DayTrialExperienceEnabled: () =>
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() === true,
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled: () =>
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() === true,
  isEvidenceLaunchBillingPageRedesignEnabled: () =>
    IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED() === true,
  useIsHeidi2PricingPlansModalEnabled,
  useIsHeidi2TeamUpgradesEnabled,
  useIsHeidi2MemberUpgradeRequestEnabled,
  useIsHeidi2PracticeMaxUpgradeEnabled,
  useIsHeidi2OptInEnabled,
  useIsHeidi2TrialEndEnabled,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsEvidenceQueryLimitsForLegacyTiersEnabled,
  useIsWorkspaceSettingsIaEnabled,
  useIsGamifyTrialExtensionEntryEnabled,
  useIsGamifyTrialArmAllowed,
  USAGE_POLICY,
  billingUrls: {
    getUsageLimitsHelpUrl,
    getBillingHelpCentreUrl,
    getBillingPlansPricingUrl,
    BILLING_PLANS_PRICING_URL,
    getGetInTouchUrl,
    GET_IN_TOUCH_URL,
    getComparePlansUrl,
    FREE_VS_PRO_PLANS_ACCOUNT_ANCHOR_HELP_ARTICLE_PATH,
  },
  getTermsOfUseLink,
  useMutateApplySubscriptionDiscount:
    useMutateApplySubscriptionDiscount as unknown as CommerceBillingUiPorts['useMutateApplySubscriptionDiscount'],
  useMutateCheckoutV2:
    useMutateCheckoutV2 as unknown as CommerceBillingUiPorts['useMutateCheckoutV2'],
  useQueryCreditAllowance:
    useQueryCreditAllowance as unknown as CommerceBillingUiPorts['useQueryCreditAllowance'],
  useQueryGamifyTrialExtension:
    useQueryGamifyTrialExtension as unknown as CommerceBillingUiPorts['useQueryGamifyTrialExtension'],
  products: {
    useQueryProducts,
    formatPriceWithCurrency,
    getCurrencySymbol,
    getDisplayedPriceFromPlan,
    getNewProductTierProduct,
    getPlanFromPeriod,
    getProductByName,
    getProductUpgradeDisplayPrices,
    getTierProduct,
  } as unknown as CommerceBillingUiPorts['products'],
  useQueryStripeLink:
    useQueryStripeLink as unknown as CommerceBillingUiPorts['useQueryStripeLink'],
  useQueryUser: useQueryUser as CommerceBillingUiPorts['useQueryUser'],
  useEvidenceBillingInfo:
    useEvidenceBillingInfo as unknown as CommerceBillingUiPorts['useEvidenceBillingInfo'],
  useFeatureLists,
  useShouldHideMarketRestrictedFeatures,
  useUpgradeModalTrialText:
    useUpgradeModalTrialText as unknown as CommerceBillingUiPorts['useUpgradeModalTrialText'],
  getCurrentPlanNameForTrial,
  useUserQuota: useUserQuota as unknown as CommerceBillingUiPorts['useUserQuota'],
  useRegion,
  isAppleCompliantBuild,
  isOnPrem,
  isTauri,
  openUrlInTauri: (url: string) => {
    void openUrlInTauri(url);
  },
  useAppRouter: useRouter,
  track: (event) => track(event as TrackEvent),
  trackingEventName: TrackingEventName,
  paywallSource: PaywallSource,
  useManualExposure:
    useManualExposure as unknown as CommerceBillingUiPorts['useManualExposure'],
  trackPracticeUpgradeModalContactSalesClick,
  trackSettingsBillingTeamUpgradeRequestSubmit,
  trackPricingPlansModalTeamUpgradeRequestSubmit,
  trackUpgradeToPracticeModalTeamUpgradeRequestSubmit,
  sleep,
  getStripeCallbackRedirect,
  getStripeReturnRedirect,
});

// Trials ports share this boot module so both registries exist before any
// lazy modal renders. Every member is a call-through; none is invoked here.
const InputEmails = lazy(() =>
  import('@/components/ui/input-emails').then((mod) => ({ default: mod.InputEmails }))
);

configureCommerceTrialsUi({
  StackedSourceAvatars: componentThrough(
    () => StackedSourceAvatars as unknown as CommerceTrialsUiPorts['StackedSourceAvatars']
  ),
  TrialStartModalBase: componentThrough(
    () => TrialStartModalBase as unknown as CommerceTrialsUiPorts['TrialStartModalBase']
  ),
  Loader: componentThrough(() => Loader as unknown as CommerceTrialsUiPorts['Loader']),
  PhoneInput: componentThrough(
    () => PhoneInput as unknown as CommerceTrialsUiPorts['PhoneInput']
  ),
  Head: componentThrough(() => Head as unknown as CommerceTrialsUiPorts['Head']),
  InputEmails: InputEmails as unknown as CommerceTrialsUiPorts['InputEmails'],
  walkthroughPosterSrc: walkthroughPoster,
  useEvidencePostUpgradeOnboardingModalOpen: () =>
    useAtom(evidencePostUpgradeOnboardingModalOpenAtom),
  useEvidencePostUpgradeWelcomeDeferQueue: () =>
    useAtom(evidencePostUpgradeWelcomeDeferQueueUntilAfterOnboardingAtom),
  useEnqueueModal: () => {
    const enqueue = useSetAtom(enqueueModalAtom);
    return (modal) => {
      enqueue(modal as Parameters<typeof enqueue>[0]);
    };
  },
  useDequeueModal: () => useSetAtom(dequeueModalAtom),
  useSeenModalIds: () => useAtom(seenModalIdsAtom),
  useQueuedModal: callThrough(
    () => useQueuedModal as unknown as CommerceTrialsUiPorts['useQueuedModal']
  ),
  useInviteTeamStepCompleted: () => useAtomValue(inviteTeamStepCompletedAtom),
  useSetPracticeTrialModalPending: () => useSetAtom(practiceTrialModalPendingAtom),
  useEvidenceAccess: callThrough(
    () => useEvidenceAccess as unknown as CommerceTrialsUiPorts['useEvidenceAccess']
  ),
  useQuerySourceRecommendations: callThrough(
    () =>
      useQuerySourceRecommendations as unknown as CommerceTrialsUiPorts['useQuerySourceRecommendations']
  ),
  useShowSessionsListAssistantView: callThrough(() => useShowSessionsListAssistantView),
  useTrialRecapUsageStats: callThrough(
    () =>
      useTrialRecapUsageStats as unknown as CommerceTrialsUiPorts['useTrialRecapUsageStats']
  ),
  usePracticeInvite: () => usePracticeInvite(),
  usePracticeInviteEmailForm: callThrough(
    () =>
      usePracticeInviteEmailForm as unknown as CommerceTrialsUiPorts['usePracticeInviteEmailForm']
  ),
  useQueryUserTier: callThrough(
    () => useQueryUserTier as unknown as CommerceTrialsUiPorts['useQueryUserTier']
  ),
  useMutateMarkModalAsViewed: () => {
    const { mutate } = useMutateMarkModalAsViewed();
    return {
      mutate:
        mutate as CommerceTrialsUiPorts['useMutateMarkModalAsViewed'] extends () => {
          mutate: infer Mutate;
        }
          ? Mutate
          : never,
    };
  },
  useMutateSubmitSalesBookingForm: () => {
    const { mutateAsync, isPending } = useMutateSubmitSalesBookingForm();
    return { mutateAsync, isPending: isPending ?? false };
  },
  isPracticeInviteFlowEnabled: () => IS_PRACTICE_INVITE_FLOW_ENABLED() === true,
  getPrivacyPolicyLink: callThrough(() => getPrivacyPolicyLink),
  getHubSpotTrackingCookie: callThrough(() => getHubSpotTrackingCookie),
  mapOrganisationSizeToHubSpot: callThrough(() => mapOrganisationSizeToHubSpot),
  normalizeCountryName: callThrough(() => normalizeCountryName),
  analytics: {
    trackPracticeTrialClick: callThrough(() => trackPracticeTrialClick),
    trackPracticeTrialViewed: callThrough(() => trackPracticeTrialViewed),
    trackPracticeInviteClick: callThrough(() => trackPracticeInviteClick),
    trackPracticeInviteViewed: callThrough(() => trackPracticeInviteViewed),
    trackTeamTrialUpgradeRequestViewed: callThrough(
      () => trackTeamTrialUpgradeRequestViewed
    ),
    trackNinetyDayTrialRecapTeamUpgradeRequestSubmit: callThrough(
      () => trackNinetyDayTrialRecapTeamUpgradeRequestSubmit
    ),
    trackClinicianTrialEndedTeamUpgradeRequestSubmit: callThrough(
      () => trackClinicianTrialEndedTeamUpgradeRequestSubmit
    ),
    trackPracticeTrialEndedTeamUpgradeRequestSubmit: callThrough(
      () => trackPracticeTrialEndedTeamUpgradeRequestSubmit
    ),
    trackNinetyDayTrialEndDiscreetClick: callThrough(
      () => trackNinetyDayTrialEndDiscreetClick
    ),
    trackNinetyDayTrialEndDiscreetViewed: callThrough(
      () => trackNinetyDayTrialEndDiscreetViewed
    ),
    trackNinetyDayTrialEndingReminderClick: callThrough(
      () => trackNinetyDayTrialEndingReminderClick
    ),
    trackNinetyDayTrialEndingReminderDismissed: callThrough(
      () => trackNinetyDayTrialEndingReminderDismissed
    ),
    trackNinetyDayTrialEndingReminderViewed: callThrough(
      () => trackNinetyDayTrialEndingReminderViewed
    ),
    trackNinetyDayTrialRecapClick: callThrough(() => trackNinetyDayTrialRecapClick),
    trackNinetyDayTrialRecapViewed: callThrough(() => trackNinetyDayTrialRecapViewed),
    trackTrialUpgradeRequestCallbackClick: callThrough(
      () => trackTrialUpgradeRequestCallbackClick
    ),
    trackTrialUpgradeRequestCallbackViewed: callThrough(
      () => trackTrialUpgradeRequestCallbackViewed
    ),
    trackClinicianTrialEndedClick: callThrough(() => trackClinicianTrialEndedClick),
    trackClinicianTrialEndedViewed: callThrough(() => trackClinicianTrialEndedViewed),
    trackEvidencePostUpgradeOnboardingClick: callThrough(
      () => trackEvidencePostUpgradeOnboardingClick
    ),
    trackEvidencePostUpgradeWelcomeClick: callThrough(
      () => trackEvidencePostUpgradeWelcomeClick
    ),
  },
});
